import React, { useReducer, useContext } from "react";
import axios from "axios";
import moment from "moment";
import DiscountContext from "./DiscountContext";
import DiscountReducer from "./DiscountReducer";
import {
  SET_DISPLAY_CONTENT,
  GET_DISCOUNTS,
  FILTER_DISCOUNTS,
  CLEAR_FILTERED_DISCOUNTS,
  SELECTED_DISCOUNT,
  FORM_DATA,
  FORM_ERROR,
  DISCOUNT_ERROR,
} from "../types";

// Contexts
import AuthContext from "../auth/AuthContext";
import LayoutContext from "../layout/LayoutContext";

const DiscountState = (props) => {
  const list_type = ["fixed_amount", "percentage"];
  const default_form_data = {
    discount_name: "",
    discount_type: list_type[0],
    discount_amount: "0.00",
    discount_startdate: moment().format("YYYY-MM-DD"),
    discount_expiredate: moment().add(1, "days").format("YYYY-MM-DD"),
    discount_include: [],
    spend_threshold: "0.00",
  };
  const default_form_error = {
    discount_name: "",
    discount_type: "",
    discount_amount: "",
    discount_startdate: "",
    discount_expiredate: "",
    discount_include: "",
    spend_threshold: "",
  };

  const initialState = {
    display_content: "",
    discounts: null,
    discounts_filtered: null,
    selected_discount: null,
    form_data: default_form_data,
    form_error: default_form_error,
    discount_error: null,
  };

  const [state, dispatch] = useReducer(DiscountReducer, initialState);
  const { user } = useContext(AuthContext);
  const { setLoading, setAlert } = useContext(LayoutContext);

  const setDisplayContent = (page) => {
    dispatch({
      type: SET_DISPLAY_CONTENT,
      payload: page,
    });
  };

  const getDiscounts = async () => {
    try {
      const res = await axios.get("/api/discount");

      dispatch({
        type: GET_DISCOUNTS,
        payload: res.data,
      });
    } catch (err) {
      dispatch({
        type: DISCOUNT_ERROR,
        payload: err.response.msg,
      });
    }
  };

  const filterDiscounts = (text) => {
    dispatch({
      type: FILTER_DISCOUNTS,
      payload: text,
    });
  };

  const clearFilteredDiscounts = () => {
    dispatch({
      type: CLEAR_FILTERED_DISCOUNTS,
    });
  };

  const getDiscount = async (code) => {
    if (
      code &&
      (!state.selected_discount ||
        state.selected_discount.discount_name !== code)
    ) {
      setLoading(true);

      try {
        const res = await axios.get(`/api/discount/${code}`);

        dispatch({
          type: SELECTED_DISCOUNT,
          payload: res.data,
        });
      } catch (err) {
        // Not Found - Return To Home
        window.location.replace(`${window.location.origin}/discount`);
        dispatch({
          type: DISCOUNT_ERROR,
          payload: err.response.msg,
        });
      }

      setLoading(false);
    }
  };

  const clearSelectedDiscount = () => {
    dispatch({
      type: SELECTED_DISCOUNT,
      payload: null,
    });
  };

  const setFormData = (data) => {
    dispatch({
      type: FORM_DATA,
      payload: data,
    });
  };

  const setFormError = (error) => {
    dispatch({
      type: FORM_ERROR,
      payload: error,
    });
  };

  const createDiscount = async () => {
    setLoading(true);

    try {
      let form_data = state.form_data;

      // Convert Date Selected Format (YYYY:MM:DD > YYYY:MM:DD hh:mm:ss)
      form_data.discount_startdate += " 00:00:00";
      form_data.discount_expiredate += " 23:59:59";

      // Convert Data Type (string > float)
      form_data.discount_amount = parseFloat(form_data.discount_amount);
      form_data.spend_threshold = parseFloat(form_data.spend_threshold);

      const res = await axios.post("/api/discount", {
        form_data,
        username: user.name,
      });

      setAlert("Discount Created.", "success");
      dispatch({
        type: SELECTED_DISCOUNT,
        payload: res.data,
      });
    } catch (err) {
      setAlert(err.response.data.msg, "error");
    }

    setLoading(false);
  };

  const updateDiscount = async (changes) => {
    setLoading(true);

    if (!changes) {
      setAlert("No Changes Has Been Made.", "info");
    } else {
      try {
        let form_data = state.form_data;

        // Convert Date Selected Format (YYYY:MM:DD > YYYY:MM:DD hh:mm:ss)
        form_data.discount_startdate += " 00:00:00";
        form_data.discount_expiredate += " 23:59:59";

        // Convert Data Type (string > float)
        form_data.discount_amount = parseFloat(form_data.discount_amount);
        form_data.spend_threshold = parseFloat(form_data.spend_threshold);

        const res = await axios.put(
          `/api/discount/${state.selected_discount.discount_name}`,
          {
            form_data,
            username: user.name,
          }
        );

        setAlert("Discount Saved.", "success");
        dispatch({
          type: SELECTED_DISCOUNT,
          payload: res.data,
        });
      } catch (err) {
        setAlert(err.response.data.msg, "error");
      }
    }

    setLoading(false);
  };

  const deleteDiscount = async () => {
    setLoading(true);

    try {
      const discount_name = state.selected_discount.discount_name;
      const name = user.name;

      const res = await axios.delete(`/api/discount/${discount_name}/${name}`);

      setAlert(res.data.msg, "success");
      // Deleted - Return To Home
      window.location.replace(`${window.location.origin}/discount`);
    } catch (err) {
      setAlert(err.response.data.msg, "error");
    }

    setLoading(false);
  };

  return (
    <DiscountContext.Provider
      value={{
        list_type,
        default_form_data,
        default_form_error,

        display_content: state.display_content,
        discounts: state.discounts,
        discounts_filtered: state.discounts_filtered,
        selected_discount: state.selected_discount,
        form_data: state.form_data,
        form_error: state.form_error,
        discount_error: state.discount_error,

        setDisplayContent,

        getDiscounts,
        filterDiscounts,
        clearFilteredDiscounts,
        getDiscount,
        clearSelectedDiscount,

        setFormData,
        setFormError,
        createDiscount,
        updateDiscount,
        deleteDiscount,
      }}
    >
      {props.children}{" "}
    </DiscountContext.Provider>
  );
};

export default DiscountState;
